import ButtonCore from "@/app/components/ui/Buttons/ButtonCore";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import PaginationComponent from "@/app/components/ui/Pagination";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useClinicTamsFilterAPI } from "@/app/hooks/api_hooks/Inventory/useClinicTamsFilterAPI";
import { useDoctorListByClinicAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToDoctorTransfer/useDoctorListByClinicAPI";
import {
  useDoctorInventoryAPI,
  type IDoctorInventoryItem,
} from "@/app/hooks/api_hooks/Inventory/DoctorInventory/useDoctorInventoryAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useMemo, useState } from "react";
import { FieldError, useForm, useWatch } from "react-hook-form";
import { clsx } from "clsx";

interface IDoctorInventoryForm {
  cityId: string;
  clinicId: string;
  doctorId: string;
}

const PAGE_SIZE = 200;

type SortKey =
  | "variant_name"
  | "category"
  | "lot_number"
  | "transfer_request_number"
  | "transferred_at"
  | "expiry_date"
  | "mrp"
  | "in_hand_quantity"
  | "available_quantity";

interface ISortState {
  key: SortKey;
  direction: "asc" | "desc";
}

const sortValue = (row: IDoctorInventoryItem, key: SortKey): string | number | null => {
  switch (key) {
    case "category":
      return row.category?.label ?? null;
    case "expiry_date":
      return row.expiry_date ? new Date(row.expiry_date).getTime() || null : null;
    default:
      return row[key] ?? null;
  }
};

// Empty values always sink to the bottom, whichever direction is chosen.
const compareRows = (a: IDoctorInventoryItem, b: IDoctorInventoryItem, sort: ISortState) => {
  const left = sortValue(a, sort.key);
  const right = sortValue(b, sort.key);
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  const result =
    typeof left === "number" && typeof right === "number"
      ? left - right
      : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" });
  return sort.direction === "asc" ? result : -result;
};

interface ISortableHeaderProps {
  label: string;
  sortKey: SortKey;
  sort: ISortState | null;
  onSort: (key: SortKey) => void;
}

const SortableHeader = ({ label, sortKey, sort, onSort }: ISortableHeaderProps) => {
  const direction = sort?.key === sortKey ? sort.direction : null;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="inline-flex items-center gap-1 font-semibold text-[#193A6A] whitespace-nowrap cursor-pointer"
      title={`Sort by ${label}`}
    >
      {label}
      <span className={clsx("text-[0.7rem]", direction ? "text-[#193A6A]" : "text-gray-400")}>
        {direction === "asc" ? "▲" : direction === "desc" ? "▼" : "↕"}
      </span>
    </button>
  );
};

const DoctorInventory = () => {
  const { cities } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const cityOptions = useSelectMenuReducer(cities, "name", "id");
  const fetchCities = useOrgCitiesListAPI("INVENTORY");

  const { execute: fetchClinics, clinicList } = useClinicTamsFilterAPI();
  const clinicOptions = useSelectMenuReducer(clinicList, "display_name", "id");

  const { execute: fetchDoctors, doctorList } = useDoctorListByClinicAPI();
  const doctorOptions = useSelectMenuReducer(doctorList, "name", "user_id");

  const { execute: fetchInventory, data, loading, reset: resetInventory } = useDoctorInventoryAPI();

  const { veticDateFormat } = useUtils();
  const { NO_PWL_RESULTS } = useImageConstants();
  const [page, setPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [sort, setSort] = useState<ISortState | null>(null);

  const handleSort = (key: SortKey) => {
    setSort((current) => {
      if (current?.key !== key) return { key, direction: "asc" };
      if (current.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  };

  const sortedItems = useMemo(() => {
    const items = data?.items ?? [];
    return sort ? [...items].sort((a, b) => compareRows(a, b, sort)) : items;
  }, [data, sort]);

  const formHook = useForm<IDoctorInventoryForm>({ mode: "onChange" });

  const selectedCity = useWatch({ control: formHook.control, name: "cityId" });
  const selectedClinic = useWatch({ control: formHook.control, name: "clinicId" });
  const selectedDoctor = useWatch({ control: formHook.control, name: "doctorId" });

  useEffect(() => {
    fetchCities();
  }, []);

  const resetSearchState = () => {
    setPage(1);
    setHasSearched(false);
    resetInventory();
  };

  useEffect(() => {
    if (selectedCity) {
      formHook.setValue("clinicId", null);
      formHook.setValue("doctorId", null);
      resetSearchState();
      fetchClinics(selectedCity, "CLINIC");
    }
  }, [selectedCity]);

  useEffect(() => {
    if (selectedClinic) {
      formHook.setValue("doctorId", null);
      resetSearchState();
      fetchDoctors(selectedClinic);
    }
  }, [selectedClinic]);

  useEffect(() => {
    resetSearchState();
  }, [selectedDoctor]);

  const handleSearch = () => {
    if (!selectedDoctor || loading) return;
    setPage(1);
    setHasSearched(true);
    fetchInventory({ doctor_ids: [selectedDoctor], page: 1, page_size: PAGE_SIZE });
  };

  const handlePageChange = (newPage: number) => {
    if (!selectedDoctor || loading) return;
    setPage(newPage);
    fetchInventory({ doctor_ids: [selectedDoctor], page: newPage, page_size: PAGE_SIZE });
  };

  const isExpired = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    return new Date(expiryDate).getTime() < Date.now();
  };

  const columns: TableColumn<IDoctorInventoryItem>[] = [
    {
      id: "variant_name",
      header: <SortableHeader label="Product Name" sortKey="variant_name" sort={sort} onSort={handleSort} />,
      width: 220,
      className: "text-[0.75rem] font-semibold text-black whitespace-normal break-words",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span>{row?.variant_name ?? "-"}</span>
          {row?.variant_transfer_count > 1 && (
            <span className="w-fit px-2 py-0.5 rounded-full bg-[#E8F0FE] text-[#193A6A] text-[0.65rem] font-medium whitespace-nowrap">
              In {row.variant_transfer_count} TOs
            </span>
          )}
        </div>
      ),
    },
    {
      id: "category",
      header: <SortableHeader label="Category" sortKey="category" sort={sort} onSort={handleSort} />,
      className: "text-[0.75rem] font-normal",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) =>
        row?.category?.label ? (
          <span
            className="px-2 py-1 rounded-full text-[0.7rem] font-medium whitespace-nowrap"
            style={{
              backgroundColor: row.category.background_color ?? "#EEE",
              color: row.category.text_color ?? "#333",
            }}
          >
            {row.category.label}
          </span>
        ) : (
          "-"
        ),
    },
    {
      id: "lot_number",
      header: <SortableHeader label="Lot #" sortKey="lot_number" sort={sort} onSort={handleSort} />,
      className: "text-[0.75rem] font-normal text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => row?.lot_number ?? "-",
    },
    {
      id: "transfer_request_number",
      header: <SortableHeader label="TO #" sortKey="transfer_request_number" sort={sort} onSort={handleSort} />,
      className: "text-[0.75rem] font-normal text-black whitespace-nowrap",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => row?.transfer_request_number ?? "-",
    },
    {
      id: "transferred_at",
      header: <SortableHeader label="TO Date" sortKey="transferred_at" sort={sort} onSort={handleSort} />,
      className: "text-[0.75rem] font-normal text-black whitespace-nowrap",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (row?.transferred_at ? veticDateFormat(row.transferred_at) : "-"),
    },
    {
      id: "expiry_date",
      header: <SortableHeader label="Expiry" sortKey="expiry_date" sort={sort} onSort={handleSort} />,
      className: "text-[0.75rem] font-semibold",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (
        <span className={clsx(isExpired(row?.expiry_date) ? "text-[#EF0303]" : "text-black")}>
          {row?.expiry_date ? veticDateFormat(row.expiry_date) : "-"}
        </span>
      ),
    },
    {
      id: "mrp",
      header: <SortableHeader label="MRP" sortKey="mrp" sort={sort} onSort={handleSort} />,
      align: "right",
      className: "text-[0.75rem] font-normal text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => (row?.mrp != null ? `₹${row.mrp}` : "-"),
    },
    {
      id: "in_hand_quantity",
      header: <SortableHeader label="In Hand" sortKey="in_hand_quantity" sort={sort} onSort={handleSort} />,
      align: "right",
      className: "text-[0.75rem] font-normal text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => row?.in_hand_quantity ?? "-",
    },
    {
      id: "blocked_inventory",
      header: "Blocked",
      align: "right",
      className: "text-[0.75rem] font-normal text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => row?.blocked_inventory ?? "-",
    },
    {
      id: "available_quantity",
      header: <SortableHeader label="Available" sortKey="available_quantity" sort={sort} onSort={handleSort} />,
      align: "right",
      className: "text-[0.75rem] font-semibold text-black",
      headerClassName: "text-[0.875rem] font-semibold text-[#193A6A]",
      cell: ({ row }) => row?.available_quantity ?? "-",
    },
  ];

  return (
    <div className="  bg-white ">
      <div className="relative px-2">
        <h3 className="text-[1rem] lg:text-[1.125rem] text-black font-semibold mb-2 sm:mb-2 2xl:mb-4">
          Doctor Inventory
        </h3>
        <form
          className="flex items-center gap-4 mt-2"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <div className="w-52">
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) formHook.setValue("cityId", item.id);
              }}
              fieldError={(formHook?.formState?.errors?.cityId as FieldError) ?? null}
              register={formHook.register("cityId")}
              selectItems={cityOptions}
              placeholder="Select City"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={cityOptions.find((c) => c.id === selectedCity)}
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-56">
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) formHook.setValue("clinicId", item.id);
              }}
              fieldError={(formHook?.formState?.errors?.clinicId as FieldError) ?? null}
              register={formHook.register("clinicId")}
              selectItems={clinicOptions}
              placeholder="Select Clinic"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={clinicOptions.find((c) => c.id === selectedClinic)}
              disabled={!selectedCity}
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div className="w-56">
            <SearchableSelectMenu
              errorMessages={[]}
              onSelectItem={(item) => {
                if (item) formHook.setValue("doctorId", item.id);
              }}
              fieldError={(formHook?.formState?.errors?.doctorId as FieldError) ?? null}
              register={formHook.register("doctorId")}
              selectItems={doctorOptions}
              placeholder="Select Doctor"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={doctorOptions.find((d) => d.id === selectedDoctor)}
              disabled={!selectedClinic}
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
          <div>
            <ButtonCore
              size="PWL"
              type="primary"
              asType="submit"
              asSubmitText={loading ? "Searching..." : "Search"}
              disabled={!selectedDoctor || loading}
              className={loading ? "cursor-not-allowed" : "cursor-pointer"}
            />
          </div>
        </form>
      </div>

      {!loading ? (
        hasSearched && data?.items?.length > 0 ? (
          <div className="relative mt-6 ml-[10px]">
            <TableComponent
              columns={columns}
              data={sortedItems}
              getRowId={(item) => item.unique_row_id}
              containerClassName=""
              scrollContainerClassName="mt-0 max-h-[calc(100vh-260px)] overflow-y-auto overflow-x-auto"
              minTableWidthClassName="min-w-full"
              tableClassName="rounded-[20px]"
              bodyClassName="text-xs"
            />
            <div className="flex justify-center items-center p-4 w-full">
              <PaginationComponent
                className="pagination-bar"
                currentPage={page}
                totalCount={data.total}
                pageSize={PAGE_SIZE}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        ) : hasSearched ? (
          <NoDataFoundComponent imageURL={NO_PWL_RESULTS} message="No inventory found for this doctor." />
        ) : (
          <NoDataFoundComponent
            imageURL={NO_PWL_RESULTS}
            message="Select a city, clinic, and doctor, then click Search to view inventory."
          />
        )
      ) : (
        <StructuredTableSkeleton columns={columns.length} className="mt-6 ml-[10px]" />
      )}
    </div>
  );
};

export default DoctorInventory;
